import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ref, onValue } from 'firebase/database';
import { managementDb } from '../firebase';
import './PortfolioGallery.css';
import PageHero from '../components/PageHero';

const PortfolioGallery = () => {
  const { filter: urlFilter } = useParams();
  const navigate = useNavigate();

  const [showAll, setShowAll] = useState(false);
  const [activeFilter, setActiveFilter] = useState(urlFilter || 'all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState('');
  const [galleryItems, setGalleryItems] = useState([]);
  const [modalIndex, setModalIndex] = useState(null);
  const filterBarRef = useRef(null);
  const filterBtnRefs = useRef({});
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0, opacity: 0 });
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  // YouTube API Key - Replace with your actual API key
  const YOUTUBE_API_KEY = 'AIzaSyDZdG7mA4zlYUZoucrLNrKeaMhPj7oqgiQ';

  useEffect(() => {
    setActiveFilter(urlFilter || 'all');
  }, [urlFilter]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Extract YouTube video ID from URL
  const getYouTubeVideoId = (url) => {
    if (!url || typeof url !== 'string') return '';
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : '';
  };

  // Convert YouTube duration format (PT1M43S) to human readable (01:43)
  const formatDuration = (duration) => {
    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    
    const hours = (parseInt(match[1]) || 0);
    const minutes = (parseInt(match[2]) || 0);
    const seconds = (parseInt(match[3]) || 0);
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    } else {
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
  };

  // Fetch gallery items from Firebase
  useEffect(() => {
    const galleryRef = ref(managementDb, 'gallery');
    const unsubscribe = onValue(galleryRef, async (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const items = Object.keys(data).map(id => ({
          id,
          ...data[id]
        })).filter(item => item.isActive).sort((a, b) => a.order - b.order);
        
                 // Extract video IDs and fetch additional data from YouTube API
         try {
           const itemsWithVideoIds = items.map(item => ({
             ...item,
             videoId: item.videoId || getYouTubeVideoId(item.videoUrl)
           }));
           
           const videoIds = itemsWithVideoIds.map(item => item.videoId).filter(id => id).join(',');
           
           if (videoIds) {
             const response = await fetch(
               `https://www.googleapis.com/youtube/v3/videos?part=snippet,contentDetails&id=${videoIds}&key=${YOUTUBE_API_KEY}`
             );
             
             const youtubeData = await response.json();
             
             // Map YouTube data to gallery items
             const itemsWithData = itemsWithVideoIds.map(item => {
               const videoData = youtubeData.items?.find(v => v.id === item.videoId);
               const duration = videoData ? formatDuration(videoData.contentDetails.duration) : 'N/A';
               
               // Generate thumbnail URL with fallback
               let thumbnail = `https://img.youtube.com/vi/${item.videoId}/maxresdefault.jpg`;
               if (videoData?.snippet?.thumbnails?.maxres?.url) {
                 thumbnail = videoData.snippet.thumbnails.maxres.url;
               } else if (videoData?.snippet?.thumbnails?.high?.url) {
                 thumbnail = videoData.snippet.thumbnails.high.url;
               } else if (videoData?.snippet?.thumbnails?.medium?.url) {
                 thumbnail = videoData.snippet.thumbnails.medium.url;
               }
               
               return { 
                 ...item,
                 duration,
                 thumbnail,
                 videoUrl: `https://www.youtube.com/embed/${item.videoId}`
               };
             });
             
             setGalleryItems(itemsWithData);

           } else {
             // Fallback if no video IDs
             const fallbackItems = itemsWithVideoIds.map(item => {
               const thumbnail = `https://img.youtube.com/vi/${item.videoId}/maxresdefault.jpg`;
               return {
                 ...item,
                 duration: 'N/A',
                 thumbnail,
                 videoUrl: `https://www.youtube.com/embed/${item.videoId}`
               };
             });
             setGalleryItems(fallbackItems);
           }
         } catch (error) {
           console.error('Error fetching YouTube data:', error);
           // Fallback with default thumbnails if API fails
           const itemsWithVideoIds = items.map(item => ({
             ...item,
             videoId: item.videoId || getYouTubeVideoId(item.videoUrl)
           }));
           
           const fallbackItems = itemsWithVideoIds.map(item => {
             const thumbnail = `https://img.youtube.com/vi/${item.videoId}/maxresdefault.jpg`;
             return {
               ...item,
               duration: 'N/A',
               thumbnail,
               videoUrl: `https://www.youtube.com/embed/${item.videoId}`
               };
             });
             setGalleryItems(fallbackItems);
           }
        } else {
          setGalleryItems([]);
        }
      });
  
      return () => unsubscribe();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

  const filteredItems = useMemo(() => {
    if (activeFilter === 'all') {
      return galleryItems.filter(item => item.category !== 'performance-marketing' && item.category !== 'production');
    }
    return galleryItems.filter(item => item.category === activeFilter);
  }, [galleryItems, activeFilter]);

  const filterOptions = [
    { key: 'all', label: 'All Projects', icon: 'fa-th-large' },
    { key: 'adfilm', label: 'Ad Films', icon: 'fa-video' },
    { key: 'brands', label: 'Brands', icon: 'fa-bullhorn' },
    { key: 'corporate', label: 'Corporate', icon: 'fa-building' },
    { key: 'documentary', label: 'Documentary', icon: 'fa-film' },
    { key: 'performance-marketing', label: 'Performance Marketing', icon: 'fa-clapperboard' },
    { key: 'production', label: 'Production', icon: 'fa-camera' }
  ];

  const activeFilterOption = filterOptions.find((option) => option.key === activeFilter) || filterOptions[0];

  const updateIndicator = useCallback(() => {
    if (window.innerWidth < 768) return;

    const bar = filterBarRef.current;
    const btn = filterBtnRefs.current[activeFilter];
    if (!bar || !btn) return;

    const barRect = bar.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();

    setIndicatorStyle({
      left: btnRect.left - barRect.left + bar.scrollLeft,
      width: btnRect.width,
      opacity: 1,
    });
  }, [activeFilter]);

  useEffect(() => {
    if (isMobile) return;

    updateIndicator();
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [updateIndicator, galleryItems, isMobile]);

  useEffect(() => {
    if (isMobile) return;

    const bar = filterBarRef.current;
    if (!bar) return;

    const onScroll = () => updateIndicator();
    bar.addEventListener('scroll', onScroll, { passive: true });
    return () => bar.removeEventListener('scroll', onScroll);
  }, [updateIndicator, isMobile]);

  useEffect(() => {
    if (isMobile) return;

    filterBtnRefs.current[activeFilter]?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    });
    updateIndicator();
  }, [activeFilter, updateIndicator, isMobile]);

  const visibleItems = showAll ? filteredItems : filteredItems.slice(0, 9);

  const openModal = (index) => {
    setModalIndex(index);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentVideo('');
    setModalIndex(null);
    document.body.style.overflow = '';
  };

  const handleModalNav = (direction) => {
    if (modalIndex === null || !filteredItems.length) return;
    const max = filteredItems.length;
    const nextIndex =
      direction === 'next'
        ? (modalIndex + 1) % max
        : (modalIndex - 1 + max) % max;
    setModalIndex(nextIndex);
  };

  useEffect(() => {
    if (modalIndex === null) return;
    const targetItem = filteredItems[modalIndex];
    if (targetItem) {
      setCurrentVideo(targetItem.videoUrl);
    } else {
      setIsModalOpen(false);
      setModalIndex(null);
    }
  }, [modalIndex, filteredItems]);

  const handleFilterClick = (category) => {
    setShowAll(false);
    if (category === 'all') {
      navigate('/gallery');
    } else {
      navigate(`/gallery/${category}`);
    }
  };


  return (
    <div className="portfolio-gallery">
      <PageHero
        title="Our Creative Portfolio"
        subtitle="Showcasing our finest productions across industries, geographies, and formats."
        align="left"
      />

      {/* Filter Section */}
      <section className="filter-section">
        <div className="filter-section-inner">
          <div className="filter-header">
            <div className="filter-header-text">
              <span className="filter-eyebrow">Browse portfolio</span>
              <h2 className="filter-title">Filter by category</h2>
            </div>
          </div>

          {isMobile ? (
            <div className="filter-dropdown-container">
              <label htmlFor="gallery-filter" className="filter-dropdown-label">
                Select category
              </label>
              <div className="filter-dropdown-wrap">
                <span className="filter-dropdown-icon" aria-hidden="true">
                  <i className={`fas ${activeFilterOption.icon}`} />
                </span>
                <select
                  id="gallery-filter"
                  className="filter-dropdown"
                  value={activeFilter}
                  onChange={(e) => handleFilterClick(e.target.value)}
                >
                  {filterOptions.map((option) => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <span className="filter-dropdown-chevron" aria-hidden="true">
                  <i className="fas fa-chevron-down" />
                </span>
              </div>
            </div>
          ) : (
            <div className="filter-scroll-wrapper">
              <div
                className="filter-container pill-bar"
                ref={filterBarRef}
                role="tablist"
                aria-label="Gallery filters"
              >
                <span
                  className="filter-indicator"
                  style={{
                    transform: `translateX(${indicatorStyle.left}px)`,
                    width: indicatorStyle.width,
                    opacity: indicatorStyle.opacity,
                  }}
                  aria-hidden="true"
                />
                {filterOptions.map((option) => (
                  <button
                    key={option.key}
                    ref={(el) => { filterBtnRefs.current[option.key] = el; }}
                    role="tab"
                    aria-selected={activeFilter === option.key}
                    aria-label={option.label}
                    className={`filter-btn ${activeFilter === option.key ? 'active' : ''}`}
                    onClick={() => handleFilterClick(option.key)}
                  >
                    <span className="filter-icon-wrap">
                      <i className={`fas ${option.icon}`} aria-hidden="true" />
                    </span>
                    <span className="filter-label">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Gallery Section */}
      <section className="gallery-section">
        {visibleItems.length === 0 ? (
          <div className="no-items-message">
            <h3>Loading gallery items...</h3>
            <p>Please wait while we load the gallery content.</p>
          </div>
        ) : (
          <div className={`gallery-container${activeFilter === 'performance-marketing' ? ' reels-only' : ''}`}> 
            {visibleItems.map((item) => {
              const itemIndex = filteredItems.findIndex(entry => entry.id === item.id);
              if (itemIndex === -1) return null;
              return (
              <div 
                key={item.id} 
                className={`gallery-item ${item.category === 'performance-marketing' ? 'reel-item' : ''} ${item.category}`}
                onClick={() => openModal(itemIndex)}
              >
                <div className="gallery-item-inner">
                  <div className={`gallery-image-container ${item.category === 'performance-marketing' ? 'reel-image-container' : ''}`}> 
                    <img 
                      src={item.thumbnail} 
                      alt={item.title}
                      onError={(e) => {
                        e.target.src = `https://img.youtube.com/vi/${item.videoId}/hqdefault.jpg`;
                      }}
                    />
                    <div className="play-btn-overlay">
                      <div className="play-btn">
                        <i className="fas fa-play"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )})}
          </div>
        )}

        {/* Load More/Less Button */}
        {filteredItems.length > 9 && (
          <div className="load-more-container">
            <button 
              onClick={() => setShowAll(!showAll)} 
              className="load-more-btn"
            >
              {showAll ? 'Show Less Projects' : 'Show More Projects'}
            </button>
          </div>
        )}

        {/* YouTube Button */}
        <div className="youtube-section">
          <h2>Explore more of our work on <span className="youtube-red">YouTube</span></h2>
          <div>
          <a href="https://www.youtube.com/@thevideocompanydotin/featured" target="_blank" rel="noopener noreferrer" style={{textDecoration: 'none'}}>
            <button className="youtube-btn">
              <i className="fab fa-youtube"></i>
              Visit YouTube Channel
            </button>
          </a>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {isModalOpen && (
        <div className="video-modal active">
          <div className="video-modal-content">
            <button className="video-nav prev" onClick={() => handleModalNav('prev')}>
              <i className="fas fa-chevron-left"></i>
            </button>
            <span className="close-modal" onClick={closeModal}>&times;</span>
            <iframe 
              src={`${currentVideo}?autoplay=1`} 
              frameBorder="0" 
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
              allowFullScreen
              title="Video Player"
            ></iframe>
            <button className="video-nav next" onClick={() => handleModalNav('next')}>
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioGallery;